import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, ClockIcon, TruckIcon, UsersIcon, PackageIcon, ShieldIcon, PlusIcon, TrashIcon, CameraIcon, XIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { useOrder } from '../context/OrderContext';
export const OrderPage = () => {
  const {
    orderDetails,
    updateOrderDetails,
    addPackage,
    updatePackage,
    removePackage,
    calculatePrice
  } = useOrder();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [newPackage, setNewPackage] = useState({
    length: 0,
    width: 0,
    height: 0,
    description: '',
    imageUrl: ''
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
  };
  const handlePackageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(3);
  };
  const handleOptionsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(4);
  };
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/confirmation');
  };
  const handleAddPackage = () => {
    if (newPackage.length > 0 && newPackage.width > 0 && newPackage.height > 0) {
      addPackage(newPackage);
      setNewPackage({
        length: 0,
        width: 0,
        height: 0,
        description: '',
        imageUrl: ''
      });
      setImagePreview(null);
    }
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real app, we would upload to a server
      // For this demo, we'll just create a local URL
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setNewPackage({
        ...newPackage,
        imageUrl
      });
    }
  };
  return <div className="bg-gray-50 min-h-screen py-10" data-id="element-238">
      <div className="container mx-auto px-4" data-id="element-239">
        <h1 className="text-3xl font-bold text-center mb-8" data-id="element-240">Book Your Move</h1>
        {/* Progress Steps */}
        <div className="mb-10" data-id="element-241">
          <div className="flex items-center justify-center mb-4" data-id="element-242">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-243">
              1
            </div>
            <div className={`h-1 w-16 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-244"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-245">
              2
            </div>
            <div className={`h-1 w-16 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-246"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-247">
              3
            </div>
            <div className={`h-1 w-16 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-248"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-249">
              4
            </div>
          </div>
          <div className="flex justify-center text-sm text-gray-600" data-id="element-250">
            <div className="w-24 text-center" data-id="element-251">Addresses</div>
            <div className="w-24 text-center" data-id="element-252">Packages</div>
            <div className="w-24 text-center" data-id="element-253">Options</div>
            <div className="w-24 text-center" data-id="element-254">Contact</div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-255">
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8" data-id="element-256">
            {/* Step 1: Addresses */}
            {step === 1 && <form onSubmit={handleAddressSubmit} data-id="element-257">
                <h2 className="text-2xl font-bold mb-6" data-id="element-258">Moving Details</h2>
                <div className="mb-6" data-id="element-259">
                  <label htmlFor="pickupAddress" className="block text-gray-700 font-medium mb-2" data-id="element-260">
                    Pickup Address
                  </label>
                  <input type="text" id="pickupAddress" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter full address" value={orderDetails.pickupAddress} onChange={e => updateOrderDetails({
                pickupAddress: e.target.value
              })} required data-id="element-261" />
                </div>
                <div className="mb-6" data-id="element-262">
                  <label htmlFor="dropoffAddress" className="block text-gray-700 font-medium mb-2" data-id="element-263">
                    Dropoff Address
                  </label>
                  <input type="text" id="dropoffAddress" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter full address" value={orderDetails.dropoffAddress} onChange={e => updateOrderDetails({
                dropoffAddress: e.target.value
              })} required data-id="element-264" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-265">
                  <div data-id="element-266">
                    <label htmlFor="moveDate" className="block text-gray-700 font-medium mb-2" data-id="element-267">
                      <CalendarIcon className="inline-block mr-1 h-5 w-5" data-id="element-268" />{' '}
                      Moving Date
                    </label>
                    <input type="date" id="moveDate" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={orderDetails.moveDate} onChange={e => updateOrderDetails({
                  moveDate: e.target.value
                })} required data-id="element-269" />
                  </div>
                  <div data-id="element-270">
                    <label htmlFor="moveTime" className="block text-gray-700 font-medium mb-2" data-id="element-271">
                      <ClockIcon className="inline-block mr-1 h-5 w-5" data-id="element-272" />{' '}
                      Preferred Time
                    </label>
                    <select id="moveTime" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={orderDetails.moveTime} onChange={e => updateOrderDetails({
                  moveTime: e.target.value
                })} required data-id="element-273">
                      <option value="" data-id="element-274">Select a time</option>
                      <option value="morning" data-id="element-275">Morning (8:00 - 12:00)</option>
                      <option value="afternoon" data-id="element-276">
                        Afternoon (12:00 - 16:00)
                      </option>
                      <option value="evening" data-id="element-277">Evening (16:00 - 20:00)</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-278">
                  <Button type="submit" data-id="element-279">Continue to Packages</Button>
                </div>
              </form>}
            {/* Step 2: Packages */}
            {step === 2 && <form onSubmit={handlePackageSubmit} data-id="element-280">
                <h2 className="text-2xl font-bold mb-6" data-id="element-281">Your Items</h2>
                <div className="mb-6" data-id="element-282">
                  <h3 className="text-lg font-medium mb-3" data-id="element-283">
                    Add Package Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-284">
                    <div data-id="element-285">
                      <label htmlFor="length" className="block text-gray-700 font-medium mb-1" data-id="element-286">
                        Length (cm)
                      </label>
                      <input type="number" id="length" min="0" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={newPackage.length || ''} onChange={e => setNewPackage({
                    ...newPackage,
                    length: parseInt(e.target.value) || 0
                  })} data-id="element-287" />
                    </div>
                    <div data-id="element-288">
                      <label htmlFor="width" className="block text-gray-700 font-medium mb-1" data-id="element-289">
                        Width (cm)
                      </label>
                      <input type="number" id="width" min="0" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={newPackage.width || ''} onChange={e => setNewPackage({
                    ...newPackage,
                    width: parseInt(e.target.value) || 0
                  })} data-id="element-290" />
                    </div>
                    <div data-id="element-291">
                      <label htmlFor="height" className="block text-gray-700 font-medium mb-1" data-id="element-292">
                        Height (cm)
                      </label>
                      <input type="number" id="height" min="0" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={newPackage.height || ''} onChange={e => setNewPackage({
                    ...newPackage,
                    height: parseInt(e.target.value) || 0
                  })} data-id="element-293" />
                    </div>
                  </div>
                  <div className="mb-4" data-id="element-294">
                    <label htmlFor="description" className="block text-gray-700 font-medium mb-1" data-id="element-295">
                      Description (optional)
                    </label>
                    <input type="text" id="description" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Sofa, Refrigerator, Box of books" value={newPackage.description} onChange={e => setNewPackage({
                  ...newPackage,
                  description: e.target.value
                })} data-id="element-296" />
                  </div>
                  <div className="mb-4" data-id="element-297">
                    <label htmlFor="image" className="block text-gray-700 font-medium mb-1" data-id="element-298">
                      Add Photo (optional)
                    </label>
                    <div className="flex items-center" data-id="element-299">
                      <label className="cursor-pointer flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-300">
                        <CameraIcon className="mr-2 h-5 w-5" data-id="element-301" />
                        Upload Photo
                        <input type="file" id="image" className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-302" />
                      </label>
                      {imagePreview && <div className="ml-4 relative" data-id="element-303">
                          <img src={imagePreview} alt="Package preview" className="h-16 w-16 object-cover rounded-md" data-id="element-304" />
                          <button type="button" className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1" onClick={() => {
                      setImagePreview(null);
                      setNewPackage({
                        ...newPackage,
                        imageUrl: ''
                      });
                    }} data-id="element-305">
                            <XIcon className="h-3 w-3" data-id="element-306" />
                          </button>
                        </div>}
                    </div>
                  </div>
                  <Button type="button" onClick={handleAddPackage} disabled={!(newPackage.length > 0 && newPackage.width > 0 && newPackage.height > 0)} data-id="element-307">
                    <PlusIcon className="mr-1 h-5 w-5" data-id="element-308" /> Add Package
                  </Button>
                </div>
                {orderDetails.packages.length > 0 && <div className="mb-8" data-id="element-309">
                    <h3 className="text-lg font-medium mb-3" data-id="element-310">Added Packages</h3>
                    <div className="bg-gray-50 rounded-md p-4" data-id="element-311">
                      {orderDetails.packages.map(pkg => <div key={pkg.id} className="flex items-center justify-between py-3 border-b last:border-0" data-id="element-312">
                          <div className="flex items-center" data-id="element-313">
                            {pkg.imageUrl && <img src={pkg.imageUrl} alt="Package" className="h-12 w-12 object-cover rounded-md mr-4" data-id="element-314" />}
                            <div data-id="element-315">
                              <div className="font-medium" data-id="element-316">
                                {pkg.description || `Package ${pkg.id.slice(-4)}`}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-317">
                                {pkg.length} × {pkg.width} × {pkg.height} cm
                              </div>
                            </div>
                          </div>
                          <button type="button" className="text-red-500 hover:text-red-700" onClick={() => removePackage(pkg.id)} data-id="element-318">
                            <TrashIcon className="h-5 w-5" data-id="element-319" />
                          </button>
                        </div>)}
                    </div>
                  </div>}
                <div className="flex justify-between" data-id="element-320">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} data-id="element-321">
                    Back
                  </Button>
                  <Button type="submit" data-id="element-322">Continue to Options</Button>
                </div>
              </form>}
            {/* Step 3: Service Options */}
            {step === 3 && <form onSubmit={handleOptionsSubmit} data-id="element-323">
                <h2 className="text-2xl font-bold mb-6" data-id="element-324">Service Options</h2>
                <div className="mb-6" data-id="element-325">
                  <label className="block text-gray-700 font-medium mb-2" data-id="element-326">
                    <TruckIcon className="inline-block mr-1 h-5 w-5" data-id="element-327" /> Vehicle
                    Size
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-328">
                    <div className={`p-4 border rounded-md cursor-pointer ${orderDetails.vehicleSize === 'small' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => updateOrderDetails({
                  vehicleSize: 'small'
                })} data-id="element-329">
                      <div className="font-medium" data-id="element-330">Small Van</div>
                      <div className="text-sm text-gray-500" data-id="element-331">
                        For small moves, up to 1-2 rooms
                      </div>
                    </div>
                    <div className={`p-4 border rounded-md cursor-pointer ${orderDetails.vehicleSize === 'medium' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => updateOrderDetails({
                  vehicleSize: 'medium'
                })} data-id="element-332">
                      <div className="font-medium" data-id="element-333">Medium Truck</div>
                      <div className="text-sm text-gray-500" data-id="element-334">
                        For medium moves, 2-3 rooms
                      </div>
                    </div>
                    <div className={`p-4 border rounded-md cursor-pointer ${orderDetails.vehicleSize === 'large' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => updateOrderDetails({
                  vehicleSize: 'large'
                })} data-id="element-335">
                      <div className="font-medium" data-id="element-336">Large Truck</div>
                      <div className="text-sm text-gray-500" data-id="element-337">
                        For large moves, 4+ rooms
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-6" data-id="element-338">
                  <label className="block text-gray-700 font-medium mb-2" data-id="element-339">
                    <UsersIcon className="inline-block mr-1 h-5 w-5" data-id="element-340" /> Extra
                    Movers
                  </label>
                  <div className="flex items-center" data-id="element-341">
                    <button type="button" className="px-3 py-1 border border-gray-300 rounded-l-md" onClick={() => updateOrderDetails({
                  extraMovers: Math.max(0, orderDetails.extraMovers - 1)
                })} data-id="element-342">
                      -
                    </button>
                    <span className="px-4 py-1 border-t border-b border-gray-300" data-id="element-343">
                      {orderDetails.extraMovers}
                    </span>
                    <button type="button" className="px-3 py-1 border border-gray-300 rounded-r-md" onClick={() => updateOrderDetails({
                  extraMovers: orderDetails.extraMovers + 1
                })} data-id="element-344">
                      +
                    </button>
                    <span className="ml-4 text-gray-600" data-id="element-345">
                      (2 movers included by default)
                    </span>
                  </div>
                </div>
                <div className="mb-6" data-id="element-346">
                  <div className="flex items-center mb-4" data-id="element-347">
                    <input type="checkbox" id="packingService" className="h-5 w-5 text-blue-600" checked={orderDetails.packingService} onChange={e => updateOrderDetails({
                  packingService: e.target.checked
                })} data-id="element-348" />
                    <label htmlFor="packingService" className="ml-2 text-gray-700" data-id="element-349">
                      <PackageIcon className="inline-block mr-1 h-5 w-5" data-id="element-350" />{' '}
                      Professional Packing Service
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-351">
                    <input type="checkbox" id="insurance" className="h-5 w-5 text-blue-600" checked={orderDetails.insurance} onChange={e => updateOrderDetails({
                  insurance: e.target.checked
                })} data-id="element-352" />
                    <label htmlFor="insurance" className="ml-2 text-gray-700" data-id="element-353">
                      <ShieldIcon className="inline-block mr-1 h-5 w-5" data-id="element-354" />{' '}
                      Moving Insurance
                    </label>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-355">
                  <h3 className="text-lg font-medium mb-2" data-id="element-356">Price Estimate</h3>
                  <div className="flex justify-between" data-id="element-357">
                    <span data-id="element-358">Base Moving Fee</span>
                    <span data-id="element-359">€50.00</span>
                  </div>
                  <div className="flex justify-between mt-2" data-id="element-360">
                    <span data-id="element-361">
                      {orderDetails.vehicleSize.charAt(0).toUpperCase() + orderDetails.vehicleSize.slice(1)}{' '}
                      Vehicle
                    </span>
                    <span data-id="element-362">
                      €
                      {orderDetails.vehicleSize === 'small' ? '50.00' : orderDetails.vehicleSize === 'medium' ? '100.00' : '150.00'}
                    </span>
                  </div>
                  {orderDetails.extraMovers > 0 && <div className="flex justify-between mt-2" data-id="element-363">
                      <span data-id="element-364">Extra Movers ({orderDetails.extraMovers})</span>
                      <span data-id="element-365">€{orderDetails.extraMovers * 25}.00</span>
                    </div>}
                  {orderDetails.packingService && <div className="flex justify-between mt-2" data-id="element-366">
                      <span data-id="element-367">Packing Service</span>
                      <span data-id="element-368">€75.00</span>
                    </div>}
                  {orderDetails.insurance && <div className="flex justify-between mt-2" data-id="element-369">
                      <span data-id="element-370">Insurance</span>
                      <span data-id="element-371">€50.00</span>
                    </div>}
                  <div className="border-t border-gray-300 my-2" data-id="element-372"></div>
                  <div className="flex justify-between font-bold" data-id="element-373">
                    <span data-id="element-374">Total Estimate</span>
                    <span data-id="element-375">€{calculatePrice()}.00</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-2" data-id="element-376">
                    Final price may vary based on actual moving conditions.
                  </div>
                </div>
                <div className="flex justify-between" data-id="element-377">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} data-id="element-378">
                    Back
                  </Button>
                  <Button type="submit" data-id="element-379">Continue to Contact</Button>
                </div>
              </form>}
            {/* Step 4: Contact Info */}
            {step === 4 && <form onSubmit={handleContactSubmit} data-id="element-380">
                <h2 className="text-2xl font-bold mb-6" data-id="element-381">Contact Information</h2>
                <div className="mb-6" data-id="element-382">
                  <label htmlFor="contactName" className="block text-gray-700 font-medium mb-2" data-id="element-383">
                    Full Name
                  </label>
                  <input type="text" id="contactName" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={orderDetails.contactName} onChange={e => updateOrderDetails({
                contactName: e.target.value
              })} required data-id="element-384" />
                </div>
                <div className="mb-6" data-id="element-385">
                  <label htmlFor="contactPhone" className="block text-gray-700 font-medium mb-2" data-id="element-386">
                    Phone Number
                  </label>
                  <input type="tel" id="contactPhone" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={orderDetails.contactPhone} onChange={e => updateOrderDetails({
                contactPhone: e.target.value
              })} required data-id="element-387" />
                </div>
                <div className="mb-6" data-id="element-388">
                  <label htmlFor="contactEmail" className="block text-gray-700 font-medium mb-2" data-id="element-389">
                    Email Address
                  </label>
                  <input type="email" id="contactEmail" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={orderDetails.contactEmail} onChange={e => updateOrderDetails({
                contactEmail: e.target.value
              })} required data-id="element-390" />
                </div>
                <div className="mb-6" data-id="element-391">
                  <div className="flex items-center" data-id="element-392">
                    <input type="checkbox" id="termsAgree" className="h-5 w-5 text-blue-600" required data-id="element-393" />
                    <label htmlFor="termsAgree" className="ml-2 text-gray-700" data-id="element-394">
                      I agree to the terms and conditions
                    </label>
                  </div>
                </div>
                <div className="flex justify-between" data-id="element-395">
                  <Button type="button" variant="outline" onClick={() => setStep(3)} data-id="element-396">
                    Back
                  </Button>
                  <Button type="submit" variant="secondary" data-id="element-397">
                    Complete Booking
                  </Button>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </div>;
};